import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Pencil, Check, FileText, MoreHorizontal, Share2, Copy, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { apiRequest } from "@/lib/queryClient";
import { formatCurrency } from "@/lib/currency";
import { getLocalizedUrl, getLocalizedInvoiceDetailUrl } from "@/lib/localized-routes";
import { TaskTypeBadge } from "@/components/task-type-badge";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

interface TaskDrawerProps {
  taskId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clients?: Array<{ id: number; businessName?: string; name?: string }>;
  teamMembers?: Array<{ id: number; memberName?: string; invitedEmail?: string }>;
  onEdit?: (taskId: number) => void;
  onMarkDone?: (taskId: number) => void;
  onCreateInvoice?: (taskId: number) => void;
  onShare?: (taskId: number) => void;
  onDuplicate?: (taskId: number) => void;
  onDelete?: (taskId: number) => void;
  user?: { isVatPayer?: boolean };
}

export function TaskDrawer({
  taskId,
  open,
  onOpenChange,
  clients = [],
  teamMembers = [],
  onEdit,
  onMarkDone,
  onCreateInvoice,
  onShare,
  onDuplicate,
  onDelete,
  user,
}: TaskDrawerProps) {
  const { t } = useTranslation(["tasks", "common"]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { data: task, isLoading } = useQuery({
    queryKey: ["/api/tasks", taskId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/tasks/${taskId}`);
      return res.json();
    },
    enabled: !!taskId && open,
  });

  const clientId = task?.clientId ?? task?.client_id;
  const assignedToId = task?.assignedToId ?? task?.assigned_to_id;
  const client = clientId ? clients.find((c) => c.id === clientId) : undefined;
  const assignedMember = assignedToId
    ? teamMembers.find((m) => m.id === assignedToId)
    : undefined;

  const total =
    task?.subtotal_cents !== undefined && task?.total_cents !== undefined
      ? user?.isVatPayer ? task.total_cents / 100 : task.subtotal_cents / 100
      : 0;

  const items = (task?.items as Array<{ name?: string; quantity?: number; unit_price?: number; amount?: number }>) || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[95vw] sm:max-w-[480px] md:max-w-[520px] flex flex-col p-0"
      >
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !task ? (
          <div className="flex-1 flex items-center justify-center p-8 text-muted-foreground">
            {t("tasks:taskNotFound") || "Úloha nebola nájdená"}
          </div>
        ) : (
          <>
            <SheetHeader className="p-4 pb-2 border-b">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-lg font-semibold flex items-center gap-2">
                    {task.isStarred && (
                      <span className="text-yellow-500">★</span>
                    )}
                    <TaskTypeBadge type={task.type || "standard"} size="sm" />
                    <span className="truncate">{task.title}</span>
                  </SheetTitle>
                  <div className="mt-1 flex flex-wrap gap-2">
                    <Badge
                      variant={
                        task.status === "completed"
                          ? "default"
                          : task.status === "in_progress"
                            ? "secondary"
                            : "outline"
                      }
                    >
                      {t(`tasks:statuses.${task.status}`)}
                    </Badge>
                    {task.isInvoiced && (
                      <Badge variant="outline" className="text-xs">
                        {t("tasks:invoiced")}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Klient */}
              {client && (
                <div>
                  <h4 className="text-xs font-medium text-foreground/80 uppercase mb-1">
                    {t("tasks:company") || "Klient"}
                  </h4>
                  <Link href={getLocalizedUrl("/clients")} className="text-primary hover:underline font-medium">
                    {client.businessName || client.name}
                  </Link>
                </div>
              )}

              {/* Meta */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-foreground/80 uppercase">
                  {t("tasks:meta") || "Termíny"}
                </h4>
                <div className="text-sm space-y-1">
                  <div>
                    <span className="text-foreground/70">Termín: </span>
                    {task.dueDate
                      ? new Date(task.dueDate).toLocaleDateString("sk-SK")
                      : "—"}
                  </div>
                  {(task.acceptDeadline ?? task.accept_deadline) && (
                    <div>
                      <span className="text-foreground/70">{t("tasks:form.acceptDeadline") || "Dátum odovzdania"}: </span>
                      {new Date(task.acceptDeadline ?? task.accept_deadline).toLocaleDateString("sk-SK")}
                    </div>
                  )}
                  {assignedMember && (
                    <div>
                      <span className="text-foreground/70">Priradená osoba: </span>
                      {assignedMember.memberName || assignedMember.invitedEmail}
                    </div>
                  )}
                </div>
              </div>

              {/* Účtovanie */}
              <div>
                <h4 className="text-xs font-medium text-foreground/80 uppercase mb-2">
                  {t("tasks:billing") || "Účtovanie"}
                </h4>
                <div className="text-sm space-y-1">
                  <div>
                    Hodiny:{" "}
                    {task.hoursWorked ?? task.actual_hours ?? task.hours_worked ?? "—"}
                  </div>
                  <div>
                    Suma: {total > 0 ? formatCurrency(total) : "—"}
                  </div>
                </div>
              </div>

              {/* Položky */}
              <div>
                <h4 className="text-xs font-medium text-foreground/80 uppercase mb-2">
                  Položky
                </h4>
                {items.length > 0 ? (
                  <div className="text-sm space-y-2 text-foreground">
                    {items.map((item: any, i: number) => (
                      <div
                        key={i}
                        className="flex justify-between py-1 border-b border-border last:border-0"
                      >
                        <span>{item.name || "—"}</span>
                        <span>
                          {item.quantity ?? 1} × {formatCurrency(item.unit_price ?? 0)} ={" "}
                          {formatCurrency(item.amount ?? (item.quantity ?? 1) * (item.unit_price ?? 0))}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-foreground/70">Bez položiek</p>
                )}
              </div>

              {/* Fakturácia */}
              {task.isInvoiced && task.invoiceId && (
                <div>
                  <h4 className="text-xs font-medium text-foreground/80 uppercase mb-1">
                    {t("tasks:invoice") || "Faktúra"}
                  </h4>
                  <Link href={getLocalizedInvoiceDetailUrl(task.invoiceId)} className="text-primary hover:underline text-sm">
                    Faktúra #{task.invoiceNumber ?? task.invoice_id ?? task.invoiceId}
                  </Link>
                </div>
              )}
            </div>

            <SheetFooter className="p-4 border-t flex flex-wrap gap-2 sm:gap-2 shrink-0">
              {onEdit && (
                <Button size="sm" onClick={() => onEdit(task.id)} className="shrink-0 whitespace-nowrap">
                  <Pencil className="h-4 w-4 mr-2 shrink-0" />
                  {t("common:edit") || "Upraviť"}
                </Button>
              )}
              {task?.status !== "completed" && onMarkDone && (
                <Button size="sm" variant="secondary" onClick={() => onMarkDone(task.id)} className="shrink-0 whitespace-nowrap">
                  <Check className="h-4 w-4 mr-2 shrink-0" />
                  Označiť hotové
                </Button>
              )}
              {!task?.isInvoiced && total > 0 && onCreateInvoice && (
                <Button size="sm" variant="secondary" onClick={() => onCreateInvoice(task.id)} className="shrink-0 whitespace-nowrap">
                  <FileText className="h-4 w-4 mr-2 shrink-0" />
                  Vytvoriť faktúru
                </Button>
              )}
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="shrink-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="z-[100]">
                  {onShare && (
                    <DropdownMenuItem onClick={() => onShare(task.id)}>
                      <Share2 className="h-4 w-4 mr-2" />
                      Zdieľať link
                    </DropdownMenuItem>
                  )}
                  {onDuplicate && (
                    <DropdownMenuItem onClick={() => onDuplicate(task.id)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Duplikovať
                    </DropdownMenuItem>
                  )}
                  {onDelete && (
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => setDeleteConfirmOpen(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {t("common:common.delete", "Vymazať")}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              {onDelete && (
                <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Zmazať úlohu?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Táto akcia je nevratná. Úloha bude natrvalo odstránená.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Zrušiť</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        onClick={() => {
                          onDelete(task.id);
                          setDeleteConfirmOpen(false);
                        }}
                      >
                        {t("common:common.delete", "Vymazať")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
